import "./dashboard.scss";
import React, { useState } from "react";
import { Card, Row, Col, Input, Empty, Spin, Button, Dropdown, Tag, Table } from "antd";
import { FileImageFilled, FilterFilled, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  CallIconDash,
  SentimentIconDash,
  TopicsIconDash,
} from "../../../assets/icons";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  getBarOptions,
  createChartPlugins,
  pieOptions,
  rangeOptions,
  sentimentData,
  sentimentScaleDefinition,
} from "./dashboardDatas";
import DateRangePicker from "../../../components/DateRangePicker";
import { useBulkDownload, useCallsTable } from "../../../hooks";
import { AppPagination } from "../../../components";
import { useNavigate } from "react-router-dom";
import { PAGE_URL } from "../../../utils";
import FilterMenu from "../../../components/filter-menu/FilterMenu";
import { callsApiService } from "../../../api/calls";
import { calculateAverageSentimentScore } from "./dashboardFunctions";
import { CallsTable } from "../../calls-data-container/callsData/CallsTable";

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  ChartDataLabels
);

export const Dashboard: React.FC = () => {

  // Destructure values and functions from the hook
  const {
    callsData,
    loading,
    search,
    dateRange,
    timezone,
    rangeType,
    callAnalytics,
    statusClassification,
    callAnalyticsLoading,
    topicAnalyticsLoading,
    topicCounts,
    topicsData,
    handleDateRangeChange,
    handleSearchChange,
    getCallAnalyticsData,
    getTopicAnalyticsData,
    handleDateRangeTypeChange,

  } = useCallsTable();

  const { handleBulkDownload, isBulkDownloading } = useBulkDownload()

  const navigate = useNavigate()
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    

    const dataWithUniqueKeys =
      callsData?.map((row, index) => ({
        ...row,
        uniqueKey: `${row.callId}-${row.fileName}-${index}`,
      })) || [];
  
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedRows(selectedRows);
    },
    getCheckboxProps: (record: any) => ({
      disabled:
        record.status !== "completed" ||
        record.statusClassification !== "SUCCESS" ||
        record.callId == null || undefined 
    }),
  };
  

  return (
    <Spin spinning={isBulkDownloading || loading} tip="Processing...">
      <div className="w-full pr">
        {/* Search and Date Range Picker Section */}
        <div className="flex jcsb aic m-b">
          <div className="flex">
            {/* Search Input */}
            <Input
              placeholder="Search by Call ID or Topics..."
              suffix={<SearchOutlined style={{ color: "#5f3cab" }} />}
              style={{ width: "300px" }}
              value={search}
              className="input-dash"
              onChange={handleSearchChange}
            />

            {/* Date Range Picker */}
            <DateRangePicker
              defaultRangeType={rangeType}
              rangeOptions={rangeOptions}
              onRangeTypeChange={handleDateRangeTypeChange}
              onDateRangeChange={handleDateRangeChange}
            />
            <Button
              className="outline ml-10"
              onClick={() => {
                getCallAnalyticsData(dateRange, timezone);
                getTopicAnalyticsData(dateRange, timezone);
              }}
            >
              {" "}
              <ReloadOutlined />{" "}
            </Button>
          </div>
        </div>

        {/* Stats Cards Section */}
        <Row gutter={40}>
          <Col span={8}>
            <Card>
              <div className="flex jic aic ml-5">
                <div>
                  <span className="icon-dash">
                    <CallIconDash className="ml-8" />
                  </span>
                </div>
                <div className="ml-10">
                  <h2>{callAnalytics.totalCalls || 0}</h2>
                  <h4>Total Processed Call Transcripts</h4>
                </div>
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <div className="flex jic aic ml-5">
                <div>
                  <span className="icon-dash">
                    <SentimentIconDash className="ml-8" />
                  </span>
                </div>
                <div className="ml-10">
                <h2>
                    {
                      ({
                        "-1": "Negative",
                        "0": "Neutral",
                        "1": "Positive",
                      } as Record<string, string>)[calculateAverageSentimentScore(callAnalytics).toString()] || "Neutral"
                    }
                  </h2>
                  <h5>Average Sentiment Score</h5>
                </div>
              </div>
          </Card>
          </Col>
          <Col span={8}>
            <Card>
              <div className="flex jic aic">
                <div>
                  <span className="icon-dash">
                    <TopicsIconDash className="ml-8" />
                  </span>
                </div>
                <div className="ml-10">
                  <h2>{statusClassification["CANDIDATE_ABSENCE_ISSUE"]}</h2>
                  <h4>Total Calls with Absent Candidate</h4>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Charts Section */}
        <Row gutter={16}>
          <Col span={12}>
            <Card title="Sentiment Distribution">
              <div style={{ height: "400px", position: "relative" }}>
                {callAnalyticsLoading ? (
                  <div style={{ paddingTop: "160px", textAlign: "center" }}>
                    <Spin />
                  </div>
                ) : callAnalytics && callAnalytics.totalCalls ? (
                  <Doughnut
                    key={JSON.stringify(callAnalytics)}
                    data={sentimentData(callAnalytics)}
                    options={pieOptions}
                    plugins={createChartPlugins(callAnalytics)}
                  />
                ) : (
                  <div style={{ paddingTop: "120px", textAlign: "center" }}>
                    <Empty />
                  </div>
                )}
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Topics Distribution">
              <div style={{ height: "400px", position: "relative" }}>
                {topicAnalyticsLoading ? (
                  <div style={{ paddingTop: "160px", textAlign: "center" }}>
                    <Spin />
                  </div>
                ) : topicsData.labels.length > 0 ? (
                  <Bar
                    data={topicsData}
                    options={getBarOptions(topicCounts)}
                    key={`topics-chart-${topicsData.labels.join("-")}`}
                  />
                ) : (
                  <div style={{ paddingTop: "120px", textAlign: "center" }}>
                    <Empty />
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Transactions Table */}
          <div className="transactions-container">
            <CallsTable isUploadButton={false} />
        </div>
      </div>
    </Spin >
  );
};
